import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { formatINR, formatPercent, signColor } from "../../lib/format";

// Premium command-style search — filters the real, already-fetched company
// and sector universe client-side. Only supports what existing data allows
// (name/ticker/sector text match), nothing fabricated.
export const DiscoverSearch = ({ companies, sectors }) => {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setFocused(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    if (!q.trim() || !companies?.length) return [];
    const s = q.trim().toLowerCase();
    return companies.filter((c) => c.ticker.toLowerCase().includes(s) || c.name.toLowerCase().includes(s) || c.sector?.toLowerCase().includes(s)).slice(0, 6);
  }, [q, companies]);

  const sectorMatches = useMemo(() => {
    if (!q.trim() || !sectors?.length) return [];
    const s = q.trim().toLowerCase();
    return sectors.filter((sec) => sec.name.toLowerCase().includes(s)).slice(0, 3);
  }, [q, sectors]);

  const showPanel = focused && q.trim().length > 0;

  return (
    <div className="relative">
      <motion.div
        animate={{
          borderColor: focused ? "rgba(245,158,11,0.4)" : "rgba(255,255,255,0.09)",
          boxShadow: focused ? "0 20px 60px -24px rgba(245,158,11,0.25)" : "0 0px 0px rgba(0,0,0,0)",
        }}
        transition={{ duration: 0.25 }}
        className="flex items-center gap-3 rounded-xl border bg-[#0B0C0E] px-5 py-4"
      >
        <Search className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <input
          data-testid="discover-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder="Search companies, sectors, or symbols…"
          aria-label="Search companies, sectors, or symbols"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="discover-search-options"
          aria-autocomplete="list"
          className="w-full bg-transparent text-base text-white outline-none placeholder:text-zinc-500"
        />
        <span className="hidden shrink-0 rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500 sm:inline">ESC</span>
      </motion.div>

      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            data-testid="discover-search-results"
            id="discover-search-options"
            role="listbox"
            className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-xl border border-white/12 bg-[#101215] shadow-2xl"
          >
            {results.length === 0 && sectorMatches.length === 0 && (
              <p className="px-5 py-6 text-center text-sm text-zinc-500">No companies or sectors match “{q}”.</p>
            )}
            {sectorMatches.length > 0 && (
              <div className="border-b border-white/10 p-2">
                <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Sectors</div>
                {sectorMatches.map((sec) => (
                  <Link role="option" aria-selected="false" key={sec.id} to={`/companies?sector=${sec.id}`} data-testid={`discover-search-sector-${sec.id}`} className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-zinc-200 transition-colors hover:bg-white/[0.06]">
                    {sec.name} <ArrowUpRight className="h-3.5 w-3.5 text-zinc-500" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            )}
            {results.length > 0 && (
              <div className="p-2">
                <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Companies</div>
                {results.map((c) => (
                  <Link role="option" aria-selected="false" key={c.ticker} to={`/company/${c.ticker}`} data-testid={`discover-search-result-${c.ticker.toLowerCase()}`} className="flex items-center justify-between gap-3 rounded-md px-3 py-2 transition-colors hover:bg-white/[0.06]">
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="shrink-0 font-mono text-xs font-semibold text-zinc-300">{c.ticker}</span>
                      <span className="truncate text-sm text-zinc-400">{c.name}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2 font-mono text-xs">
                      <span className="text-white">{formatINR(c.price)}</span>
                      <span className={signColor(c.change_percent)}>{formatPercent(c.change_percent)}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
